/**
 * MessageListTable - Paginated data table for message threads
 *
 * Reuses UI columns from the original MessageListTable.
 * All logic comes from useMessageList & useMessageFilters hooks.
 */

import React, { useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { Link, useRouteMatch } from 'react-router-dom';
import { classNames } from 'primereact/utils';

import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import ImageComponent from 'shared/ImageComponent';
import GlobalTooltip from 'components/GlobalTooltip';
import { SentimentDot } from 'components/SentimentDot/SentimentDot';

import { useUserDetailsContext } from 'context/userDetailsContext';
import { useMessageContext } from '../../context';
import { useUpdateReadPointer } from '../../hooks';
import { useNavbarContext } from 'context/NavbarContext';
import { getSignedUrl } from '../../services/messageService';

import { replaceLawftDate } from 'utils/utils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { handlePageNumberInPagination } from 'common/pagination';
import { truncateText } from 'utils/utility_functions/textTruncate';
import { generateFullName } from 'utils/generateFullNameUtils';
import {
  MESSAGE_PREVIEW_MAX_LENGTH,
  TABLE_MESSAGES,
  PAGINATION_CONFIG,
  PAGINATION_PATHS,
  TOOLTIPS,
  FIELD_TO_SORT_KEY,
} from '../../constants/constants';
import constants from 'constants/index';

function MessageListTable({ messages, totalCount, currentPage, isLoading, filterState }) {
  const userContext = useUserDetailsContext();
  const { downloadFile } = useNavbarContext();
  const { isGlobal, isFromClientPortal, isFromLead, isFromCase } = useMessageContext();
  const match = useRouteMatch();

  const updateReadPointerMutation = useUpdateReadPointer();
  // ── Mark read/unread (using new unified read pointer API) ────────────────

  const handleMarkReadUnread = useCallback(
    (e, rowData, isRead) => {
      e.preventDefault();
      if (!rowData?.message_thread_id) return;

      const userId = userContext?.userDetails?.firm_user_id || userContext?.userDetails?.contact_id;

      if (!userId) {
        console.warn('Cannot update read status: missing user ID');
        return;
      }

      updateReadPointerMutation.mutate({
        message_thread_id: rowData.message_thread_id,
        user_id: userId,
        message_type: 'reply',
        unread: isRead, // Toggle: if currently read, mark as unread
      });
    },
    [updateReadPointerMutation, userContext]
  );

  // ── Attachment download ───────────────────────────────────────────────────

  const handleDownloadAttachment = useCallback(
    async (e, fileInfo) => {
      e.preventDefault();
      const link = await getSignedUrl(fileInfo);
      downloadFile(link, fileInfo.display_name);
    },
    [downloadFile]
  );

  // ── Sorting ───────────────────────────────────────────────────────────────

  const handleSort = useCallback(
    (e) => {
      const sortBy = FIELD_TO_SORT_KEY[e.sortField] || e.sortField;
      const sortOrder = e.sortOrder === 1 ? 'asc' : 'desc';
      filterState.handleSortChange({ sortBy, sortOrder });
    },
    [filterState]
  );

  // ── Pagination ────────────────────────────────────────────────────────────

  const handlePageChange = useCallback(
    (page) => {
      filterState.handlePageChange(page);
    },
    [filterState]
  );

  const handlePageSizeChange = useCallback(
    (newSize) => {
      filterState.handlePageSizeChange(newSize);
      const path = getFilePathForPagination();
      if (path) handlePageNumberInPagination(userContext, path.module, path.subModule, path.listName, newSize);
    },
    [filterState, userContext]
  );

  function getFilePathForPagination() {
    if (isFromLead) return PAGINATION_PATHS.LEADS;
    if (isFromCase) return PAGINATION_PATHS.CASE;
    if (isGlobal) return PAGINATION_PATHS.MESSAGES;
    if (isFromClientPortal) return PAGINATION_PATHS.CLIENT_PORTAL;
    return null;
  }

  // ── Column Templates (memoized to prevent re-creation on every render) ────

  const subjectTemplate = useCallback(
    (rowData) => {
      const stripHtml = (html) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        const items = div.querySelectorAll('li');
        if (items.length > 0)
          return Array.from(items)
            .map((li, i) => `${i + 1}. ${li.textContent}`)
            .join('\n');
        return div.textContent || '';
      };

      return (
        <>
          <span className="p-column-title text-break">Subject</span>
          <div>
            <Link to={`${match.url}/${rowData?.message_thread_id}`} className="redirection-link-no-text-transform">
              <div>
                {rowData?.scheduled_status === 'scheduled' && <i className="far fa-clock" style={{ color: 'var(--icon-red)' }} />}
                <div
                  className={`pointer text-break text-primary-dark flex-grow-1 subject-truncate ${
                    !rowData?.is_read || (!isFromClientPortal && rowData?.is_new_sidebar_message) ? 'text-bold text-capitalize' : ''
                  }`}
                  title={rowData?.subject}
                >
                  {rowData?.subject}
                </div>
              </div>
            </Link>
            {rowData.reply_count ? (
              <span className="text-hint black-600 r-me-0" style={{ fontSize: 'small' }}>
                {parseInt(rowData.reply_count) > 1 ? `${parseInt(rowData.reply_count)} Replies` : '1 Reply'}
              </span>
            ) : null}
            {rowData?.message && (
              <div className="text-muted mt-2 d-flex align-items-center">
                {rowData?.scheduled_reply && <i className="far fa-clock me-2" style={{ color: 'var(--icon-red)' }} />}
                {(() => {
                  const msg = replaceLawftDate(stripHtml(rowData.message), userContext?.userDetails?.timezone);
                  return (
                    <span className="truncate-2 text-break" title={msg}>
                      {msg?.length > MESSAGE_PREVIEW_MAX_LENGTH ? `${msg.substring(0, MESSAGE_PREVIEW_MAX_LENGTH)}...` : msg}
                    </span>
                  );
                })()}
              </div>
            )}
          </div>
        </>
      );
    },
    [userContext?.userDetails?.timezone]
  );

  const authorTemplate = useCallback((rowData) => {
    const authorName = rowData?.author_details ? generateFullName(rowData?.author_details) : rowData?.author_name;
    return (
      <>
        <span className="p-column-title text-break">Author</span>
        <div className="d-inline-table text-break" style={{ cursor: 'text' }}>
          <div className="d-flex flex-nowrap align-items-center">
            <ImageComponent
              filePath={rowData?.created_by_user_image}
              fileName={authorName}
              fileSize="medium.jpg"
              avatarSize="medium"
              style={{ cursor: 'text', borderRadius: '50%' }}
            />
            <div className="ms-1 message-author-ellipsis-text">
              {rowData.author_details?.firm_user_id && <i className="fas fa-user-tie me-2" />}
              <span title={authorName}>{authorName}</span>
            </div>
          </div>
        </div>
      </>
    );
  }, []);

  const statusTemplate = useCallback(
    (rowData) => (
      <>
        <span className="p-column-title text-break">Status</span>
        <div className="d-inline-table message-status">
          <GlobalTooltip tooltip={rowData?.is_new ? TOOLTIPS.NEW_MESSAGE : 'No new replies in message thread'}>
            <i className={`icon-messages me-3 ${rowData?.is_new ? 'new-message-notification' : 'icon-disable disabled-message-icon'}`}>
              {rowData?.is_new && <i className="icon-window-filled" />}
            </i>
          </GlobalTooltip>

          {!isFromClientPortal && (
            <GlobalTooltip tooltip={rowData?.is_new_sidebar_message ? TOOLTIPS.NEW_SIDEBAR_MESSAGE : TOOLTIPS.NO_NEW_SIDEBAR_MESSAGE}>
              <i
                className={`icon-sidebar ${
                  rowData?.is_new_sidebar_message ? 'new-sidebar-notification' : 'icon-disable disabled-sidebar-icon'
                } me-3`}
              >
                {rowData?.is_new_sidebar_message && <i className="icon-window-filled"></i>}
              </i>
            </GlobalTooltip>
          )}

          {!isFromClientPortal && rowData?.resolution_status && !rowData?.resolved_status && (
            <GlobalTooltip tooltip="Need resolution">
              <i className="bi bi-exclamation-circle me-3" />
            </GlobalTooltip>
          )}

          {!isFromClientPortal && rowData?.resolved_status && (
            <GlobalTooltip tooltip="Resolved">
              <i className="bi bi-check-circle icon-disable me-3" />
            </GlobalTooltip>
          )}
        </div>
      </>
    ),
    [isFromClientPortal]
  );

  const repliesTemplate = useCallback(
    (rowData) => (
      <>
        <span className="p-column-title text-break">Most Recent Replies</span>
        <div className="d-inline-table text-break">
          <p className="mb-1 F-size14 black-600">
            {convertDateFromApiToUserTimezone(
              rowData?.created_at,
              userContext?.userDetails?.timezone,
              constants.month_date_year_time_12_format
            )}
          </p>
          {rowData.last_reply && (
            <p className="text-hint black-600 r-me-0">
              Last Reply:{' '}
              {convertDateFromApiToUserTimezone(
                rowData?.last_reply,
                userContext?.userDetails?.timezone,
                constants.month_date_year_time_12_format
              )}
            </p>
          )}
        </div>
      </>
    ),
    [userContext?.userDetails?.timezone]
  );

  const stateTemplate = useCallback(
    (rowData) => {
      const { is_read } = rowData || {};
      return (
        <>
          <span className="p-column-title text-break">State</span>
          <div className="status-mark d-inline-table">
            <a
              href="/"
              onClick={(e) => handleMarkReadUnread(e, rowData, is_read)}
              className={`F-size12 text-decoration-none call-back`}
              title={is_read ? 'Mark as Unread' : 'Mark as Read'}
            >
              {is_read ? 'Mark as Unread' : <span>Mark as Read</span>}
            </a>
          </div>
        </>
      );
    },
    [handleMarkReadUnread, userContext]
  );

  const sentimentTemplate = useCallback(
    (rowData) => (
      <>
        <span className="p-column-title text-break">Sentiment</span>
        <div className="d-inline-table">
          <SentimentDot
            type={rowData?.aggregate_sentiment}
            positive={rowData?.positive_sentiment_count}
            neutral={rowData?.neutral_sentiment_count}
            negative={rowData?.negative_sentiment_count}
            mixed={rowData?.mixed_sentiment_count}
          />
        </div>
      </>
    ),
    []
  );

  const caseNameTemplate = useCallback(
    (rowData) => {
      const navigate = () => {
        const mainModule = rowData?.is_lead ? '/leads' : '/cases';
        return rowData?.case_id ? `${mainModule}/${rowData.case_id}` : mainModule;
      };

      return (
        <>
          <span className="p-column-title text-break">Case Name</span>
          <Link
            to={navigate}
            className={`text-decoration-none ${classNames({
              'redirection-link-no-text-transform': rowData.case_name && !isFromClientPortal,
              'pe-none': isFromClientPortal,
            })}`}
            title={rowData?.case_name}
          >
            {rowData?.case_name || '-'}
          </Link>
        </>
      );
    },
    [isFromClientPortal]
  );

  const attachmentsTemplate = useCallback(
    (rowData) => (
      <>
        <span className="p-column-title text-break">Attachments</span>
        <div className="upload-document d-inline-table">
          {rowData?.attachment?.length ? (
            <p className="pointer text-break mb-0" onClick={(e) => handleDownloadAttachment(e, rowData.attachment[0])}>
              <i className="fas fa-file-pdf redirection-link-no-text-transform" />
              <span className="ms-1 redirection-link-no-text-transform" title={rowData.attachment[0]?.file_name}>
                {truncateText(rowData.attachment[0]?.file_name, 20)}
              </span>
            </p>
          ) : (
            '-'
          )}
        </div>
      </>
    ),
    [handleDownloadAttachment]
  );

  // ── Custom Pagination Footer ──────────────────────────────────────────────

  const paginationFooter = useMemo(
    () => (
      <EllipsisPagination
        currentPage={currentPage}
        totalRecords={totalCount}
        pageSize={filterState.pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showPageSizeDropdown={true}
      />
    ),
    [currentPage, totalCount, filterState.pageSize, handlePageChange, handlePageSizeChange]
  );

  // ── Columns config ────────────────────────────────────────────────────────

  const columns = useMemo(
    () => [
      { field: 'subject', header: 'Subject', body: subjectTemplate, sortable: true },
      { field: 'author_details.first_name', header: 'Author', body: authorTemplate, sortable: true, style: { cursor: 'text' } },
      {
        field: 'case_name',
        header: 'Case Name',
        body: caseNameTemplate,
        sortable: true,
        bodyClassName: 'ellipsis-text',
        hidden: !isGlobal,
      },
      { field: 'is_new', header: 'Status', body: statusTemplate, sortable: true },
      { field: 'last_reply', header: 'Most Recent Replies', body: repliesTemplate, sortable: true },
      { field: 'attachment', header: 'Attachments', body: attachmentsTemplate, sortable: true, bodyClassName: 'ellipsis-text' },
      {
        field: 'aggregate_sentiment',
        header: 'Sentiment',
        body: sentimentTemplate,
        sortable: true,
        className: 'md-align-center',
        headerStyle: { textAlign: 'center' },
        hidden: isFromClientPortal,
      },
      { field: 'is_read', header: '', body: stateTemplate, bodyClassName: 'ellipsis-text' },
    ],
    [
      subjectTemplate,
      authorTemplate,
      caseNameTemplate,
      statusTemplate,
      repliesTemplate,
      attachmentsTemplate,
      sentimentTemplate,
      stateTemplate,
      isGlobal,
      isFromClientPortal,
    ]
  );

  // Reverse lookup: BE sort_by key → DataTable field (for controlled sort indicator)
  const SORT_KEY_TO_FIELD = useMemo(() => Object.fromEntries(Object.entries(FIELD_TO_SORT_KEY).map(([field, key]) => [key, field])), []);

  const activeSortField = filterState.sortBy ? SORT_KEY_TO_FIELD[filterState.sortBy] || filterState.sortBy : null;
  const activeSortOrder = filterState.sortOrder === 'asc' ? 1 : -1;

  return (
    <div className="overflow-auto bg-white shadow-middle mb-3">
      <div className="datatable-responsive">
        <ResizableDataTable
          value={messages}
          breakpoint="960px"
          responsiveLayout="scroll"
          className="cursor-pointer p-datatable-responsive"
          emptyMessage={isLoading ? TABLE_MESSAGES.LOADING : TABLE_MESSAGES.NO_DATA}
          stripedRows
          columns={columns}
          storageKey={PAGINATION_CONFIG.STORAGE_KEY}
          sortMode="single"
          sortField={activeSortField}
          sortOrder={activeSortOrder}
          onSort={handleSort}
        />
        {paginationFooter}
      </div>
    </div>
  );
}

MessageListTable.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      sk: PropTypes.string,
      message_thread_id: PropTypes.string,
      subject: PropTypes.string,
      message: PropTypes.string,
      case_id: PropTypes.string,
      case_name: PropTypes.string,
      is_read: PropTypes.bool,
      is_new: PropTypes.bool,
      created_at: PropTypes.string,
      author_details: PropTypes.object,
      attachment: PropTypes.array,
    })
  ),
  totalCount: PropTypes.number,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  isLoading: PropTypes.bool,
  filterState: PropTypes.shape({
    pageSize: PropTypes.number,
    handlePageChange: PropTypes.func,
    handlePageSizeChange: PropTypes.func,
    handleSortChange: PropTypes.func,
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
  }).isRequired,
};

MessageListTable.defaultProps = {
  messages: [],
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  isLoading: false,
};

export default memo(MessageListTable);
